// A content photo (e.g. a CELPIP speaking scene). The image is generated at
// deploy and dropped at media/images/<imageRef>; until then a clean framed
// placeholder reserves the slot, and the caption (the scene description) keeps
// the task understandable. The real photo drops straight into this slot the
// moment the file exists, no other change anywhere. Mirrors the audio-slot
// pattern: the asset is fetched as an authenticated blob, never a bare <img src>.
import React, { useEffect, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';

import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

export default function ContentImage({ imageRef, caption, compact = false, className }) {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    setUrl(null);
    if (!imageRef) return undefined;
    let alive = true;
    let obj = null;
    api.contentImageBlob(imageRef)
      .then((res) => { if (!alive) return; obj = URL.createObjectURL(res.data); setUrl(obj); })
      .catch(() => { if (alive) setUrl(null); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [imageRef]);

  if (url) {
    return <img src={url} alt={caption || 'Scene'} className={cn('w-full rounded-md', className)} data-testid="content-image" />;
  }

  // Clean reserved slot until the photo exists.
  return (
    <figure
      data-testid="content-image-placeholder"
      className={cn('overflow-hidden rounded-md border border-dashed border-slate-300 bg-slate-50', className)}
    >
      <div className={cn('flex flex-col items-center justify-center gap-1.5 text-center', compact ? 'px-3 py-4' : 'px-6 py-8')}>
        <ImageIcon className={cn('text-slate-300', compact ? 'h-5 w-5' : 'h-7 w-7')} />
        <p className={cn('font-medium uppercase tracking-wide text-slate-400', compact ? 'text-[8px]' : 'text-[11px]')}>
          Picture appears here
        </p>
      </div>
      {caption && (
        <figcaption className={cn('border-t border-dashed border-slate-200 bg-white text-slate-600',
          compact ? 'px-2.5 py-1.5 text-[8.5px] leading-snug' : 'px-4 py-3 text-[13px] leading-6')}>
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
